import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
} from "chart.js";
import { Line } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";

import { Info } from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement);

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;


const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;


const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const MonthlySalesComparison = ({ data }) => {
  const [salesData, setSalesData] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  const obliczSprzedazMiesięczna = (row) =>
    parseFloat(row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22) || 0;

  useEffect(() => {
    if (data && data.length > 0) {
      const grouped = {};

      data.forEach((row) => {
        const year = parseInt(row.Rok, 10);
        const month = parseInt(row.Miesiac, 10);
        const value = obliczSprzedazMiesięczna(row);

        if (!grouped[year]) grouped[year] = {};
        if (!grouped[year][month]) grouped[year][month] = 0;
        grouped[year][month] += value;
      });

      const years = Object.keys(grouped).map((y) => parseInt(y, 10));
      const latestYear = Math.max(...years);

      setSalesData(grouped);
      setAvailableYears(years.sort((a, b) => b - a));
      setSelectedYear(latestYear);
    }
  }, [data]);

useEffect(() => {
  if (selectedYear && salesData) {
    const currentYearData = Array.from({ length: 12 }, (_, i) => salesData[selectedYear]?.[i + 1] || 0);
    const previousYearData = Array.from({ length: 12 }, (_, i) => salesData[selectedYear - 1]?.[i + 1] || 0);

    setChartData({
      labels: monthNames,
      datasets: [
        {
          label: `Sprzedaż ${selectedYear} (PLN)`,
          data: currentYearData,
          borderColor: "#2e7d32",              // ✅ current year = green
          backgroundColor: "rgba(46,125,50,0.2)",
          fill: true,
          pointBackgroundColor: "#2e7d32",
          tension: 0.4,
        },
        {
          label: `Sprzedaż ${selectedYear - 1} (PLN)`,
          data: previousYearData,
          borderColor: "#fb8c00",              // ✅ previous year = orange
          backgroundColor: "rgba(251,140,0,0.2)",
          fill: true,
          pointBackgroundColor: "#fb8c00",
          tension: 0.4,
        },
      ],
    });
  }
}, [selectedYear, salesData]);


  if (!chartData) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
<InfoIconWrapper data-tooltip-id="monthly-sales-tooltip">
    <Info />
  </InfoIconWrapper>

<InfoTooltip
  id="monthly-sales-tooltip"
  place="left"
  content={
    <>
      <strong>Sprzedaż miesięczna</strong>
      <br />
      Ten wykres pokazuje wartość sprzedaży w każdym miesiącu,<br />
      porównując rok {selectedYear} z {selectedYear - 1}.
    </>
  }
/>

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

     <Line
  data={chartData}
options={{
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: "Sprzedaż",
      },
      ticks: {
        callback: function (value) {
          return Number(value)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
        },
      },
    },
  },
  plugins: {
    title: {
      display: true,
      text: `Sprzedaż miesięczna: ${selectedYear} vs ${selectedYear - 1}`,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const label = context.dataset.label || '';
          const value = context.parsed.y || 0;
          const formattedValue = Number(value)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
          return `${label}: ${formattedValue}`;
        },
      },
    },
  },
}}

/>

    </ChartWrapper>
  );
};

export default MonthlySalesComparison;
